import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Search, FolderPlus, Upload, Loader, Palette, X, CheckSquare, Square, Trash2 } from "lucide-react";
import { authFetch, authFetchJson, apiUrl } from "../../utils/api";
import FileItem from "./FileItem";
import "./FileBrowser.css";

// Folder colors - matches ConversationSidebar
const FOLDER_COLORS = [
  "#007AFF", // Indigo (default)
  "#007AFF", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#3b82f6", // Blue
  "#64748b", // Slate
];

/**
 * FileBrowser - VFS file tree view for MinIO storage
 *
 * Displays user's MinIO bucket contents as expandable tree.
 * Features:
 * - Expandable folders with color coding
 * - Drag & drop to move files between folders
 * - File icons by type
 * - Search by filename
 * - Upload via button or drag/drop anywhere
 * - Context menu (rename, delete, download, copy path)
 */
const FileBrowser = ({ userId, isCollapsed, onUseInChat }) => {
  const { getToken } = useAuth();
  const [files, setFiles] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set([""]));
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState(null); // For rate limit warnings
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Transcription job tracking (for audio/video uploads)
  const [pendingJobs, setPendingJobs] = useState(new Map()); // jobId -> { filename, progress, status }
  const pollIntervalRef = useRef(null);

  // New folder state
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const newFolderInputRef = useRef(null);

  // Folder color modal state
  const [showColorModal, setShowColorModal] = useState(false);
  const [colorModalFolder, setColorModalFolder] = useState(null);

  // Share-with-team modal state (folders → teams, r/w for members + agents)
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalFolder, setShareModalFolder] = useState(null);
  const [myTeams, setMyTeams] = useState(null); // null = not loaded
  const [folderShares, setFolderShares] = useState({}); // teamId -> Set(folder_path)
  const [shareBusy, setShareBusy] = useState(false);
  const [shareMsg, setShareMsg] = useState(null);

  const openShareModal = async (folderPath) => {
    setShareModalFolder(folderPath);
    setShareMsg(null);
    setShowShareModal(true);
    try {
      const data = await authFetchJson(apiUrl("/api/teams"));
      const teams = data.teams || [];
      setMyTeams(teams);
      const shares = {};
      await Promise.all(teams.map(async (t) => {
        try {
          const sh = await authFetchJson(apiUrl(`/api/teams/${t.id}/shared`));
          shares[t.id] = new Set((sh.shared?.folders || []).map((f) => f.folder_path));
        } catch { shares[t.id] = new Set(); }
      }));
      setFolderShares(shares);
    } catch {
      setMyTeams([]);
      setShareMsg("Couldn't load your teams — create one in Settings → Team first.");
    }
  };

  const toggleFolderShare = async (teamId, folderPath, isShared) => {
    setShareBusy(true);
    setShareMsg(null);
    try {
      if (isShared) {
        await authFetchJson(apiUrl("/api/teams/unshare"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resource_type: "folder", resource_id: folderPath }),
        });
      } else {
        await authFetchJson(apiUrl(`/api/teams/${teamId}/share`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resource_type: "folder", resource_id: folderPath }),
        });
      }
      setFolderShares((prev) => {
        const next = { ...prev };
        const set = new Set(next[teamId] || []);
        if (isShared) set.delete(folderPath); else set.add(folderPath);
        next[teamId] = set;
        return next;
      });
      setShareMsg(isShared ? "Unshared." : "Shared — your team and their agents now have read/write here.");
    } catch (e) {
      setShareMsg(e?.message || "Share failed");
    } finally {
      setShareBusy(false);
    }
  };

  // Multi-select state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [lastSelectedPath, setLastSelectedPath] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [folderColors, setFolderColors] = useState(() => {
    // Load folder colors from localStorage
    try {
      const saved = localStorage.getItem(`folder_colors_${userId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save folder colors to localStorage when they change
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`folder_colors_${userId}`, JSON.stringify(folderColors));
    }
  }, [folderColors, userId]);

  // Fetch file tree from API
  const fetchFiles = useCallback(async () => {
    if (!userId) return;

    try {
      setError(null);
      const response = await authFetchJson(apiUrl("/api/c3/files"));

      if (response.success) {
        setFiles(response.files || []);
      } else {
        setError(response.error || "Failed to load files");
      }
    } catch (err) {
      console.error("[FileBrowser] Error fetching files:", err);
      setError("Failed to connect to file server");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // "Shared with me" — team folders. Lazy: fires after mount, never blocks
  // the user's own tree; failures collapse the section silently.
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [sharedOpen, setSharedOpen] = useState({}); // key -> {loading, files}
  useEffect(() => {
    (async () => {
      try {
        const data = await authFetchJson(apiUrl("/api/teams/shared-folders"));
        setSharedWithMe(data.folders || []);
      } catch (_) { /* no teams / feature off */ }
    })();
  }, []);

  const toggleSharedFolder = async (f) => {
    const key = `${f.team_id}/${f.folder_path}`;
    setSharedOpen((prev) => {
      if (prev[key]) { const n = { ...prev }; delete n[key]; return n; }
      return { ...prev, [key]: { loading: true, files: [] } };
    });
    if (sharedOpen[key]) return; // was open → just closed
    try {
      const data = await authFetchJson(apiUrl(`/api/fs/list?prefix=${encodeURIComponent(`team/${f.team_id}/${f.folder_path}`)}`));
      setSharedOpen((prev) => prev[key] ? { ...prev, [key]: { loading: false, files: data.files || [] } } : prev);
    } catch (_) {
      setSharedOpen((prev) => prev[key] ? { ...prev, [key]: { loading: false, files: [] } } : prev);
    }
  };

  const downloadSharedFile = async (path) => {
    try {
      const res = await authFetch(apiUrl(`/api/fs/read?path=${encodeURIComponent(path)}`));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = path.split("/").pop();
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { console.error("[FileBrowser] shared download failed", e); }
  };

  // Warn user before leaving during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = 'Upload in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  // Expose upload state globally so sidebar mode toggle can check it
  useEffect(() => {
    window.__fileBrowserUploading = uploading;
    return () => { window.__fileBrowserUploading = false; };
  }, [uploading]);

  // Poll for transcription job status
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await authFetchJson(apiUrl(`/api/c3/transcription/status/${jobId}`));
      if (response.success && response.job) {
        const job = response.job;

        if (job.status === 'completed') {
          // Job finished - remove from pending, clear message, and refresh files
          setPendingJobs(prev => {
            const next = new Map(prev);
            next.delete(jobId);
            return next;
          });
          setUploadMessage(null); // Clear the "Transcribing..." message
          fetchFiles();
          console.log(`[FileBrowser] Transcription completed: ${job.filename}`);
        } else if (job.status === 'failed') {
          // Job failed - show error and remove
          setPendingJobs(prev => {
            const next = new Map(prev);
            next.delete(jobId);
            return next;
          });
          setUploadMessage(`Transcription failed: ${job.error || 'Unknown error'}`);
          setTimeout(() => setUploadMessage(null), 10000);
          console.error(`[FileBrowser] Transcription failed: ${job.filename}`, job.error);
        } else {
          // Still processing - update progress
          setPendingJobs(prev => {
            const next = new Map(prev);
            const existing = prev.get(jobId) || {};
            next.set(jobId, { ...existing, progress: job.progress, status: job.status });
            return next;
          });
        }
      }
    } catch (err) {
      console.warn(`[FileBrowser] Error polling job ${jobId}:`, err.message);
    }
  }, [fetchFiles]);

  // Start/stop polling when pending jobs change
  useEffect(() => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Start polling if we have pending jobs
    if (pendingJobs.size > 0) {
      pollIntervalRef.current = setInterval(() => {
        pendingJobs.forEach((_, jobId) => {
          pollJobStatus(jobId);
        });
      }, 2000); // Poll every 2 seconds
    }

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [pendingJobs, pollJobStatus]);

  // Focus new folder input when shown
  useEffect(() => {
    if (showNewFolderInput && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [showNewFolderInput]);

  // Refresh handler
  const handleRefresh = () => {
    setRefreshing(true);
    fetchFiles();
  };

  // Toggle folder expansion
  const toggleFolder = (path) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Get folder color
  const getFolderColor = (path) => {
    return folderColors[path] || FOLDER_COLORS[0];
  };

  // Set folder color
  const setFolderColor = (path, color) => {
    setFolderColors((prev) => ({ ...prev, [path]: color }));
    setShowColorModal(false);
    setColorModalFolder(null);
  };

  // Open color picker modal
  const openColorModal = (folderPath) => {
    setColorModalFolder(folderPath);
    setShowColorModal(true);
  };

  // ============ MULTI-SELECT HANDLERS ============

  // Toggle selection mode
  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Exiting selection mode - clear selections
      setSelectedFiles(new Set());
      setLastSelectedPath(null);
    }
    setSelectionMode(!selectionMode);
  };

  // Handle file selection (with Shift for range, Ctrl/Cmd for toggle)
  const handleFileSelect = (path, event) => {
    const allPaths = files.filter(f => f.type === "file").map(f => f.path);

    if (event?.shiftKey && lastSelectedPath) {
      // Range selection
      const lastIndex = allPaths.indexOf(lastSelectedPath);
      const currentIndex = allPaths.indexOf(path);
      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        const rangePaths = allPaths.slice(start, end + 1);
        setSelectedFiles(prev => {
          const next = new Set(prev);
          rangePaths.forEach(p => next.add(p));
          return next;
        });
      }
    } else if (event?.metaKey || event?.ctrlKey) {
      // Toggle individual selection
      setSelectedFiles(prev => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
        }
        return next;
      });
      setLastSelectedPath(path);
    } else {
      // Single selection (toggle)
      setSelectedFiles(prev => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
        }
        return next;
      });
      setLastSelectedPath(path);
    }
  };

  // Select all files
  const selectAll = () => {
    const allFilePaths = files.filter(f => f.type === "file").map(f => f.path);
    setSelectedFiles(new Set(allFilePaths));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFiles(new Set());
    setLastSelectedPath(null);
  };

  // Bulk delete selected files
  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) return;

    const confirmed = window.confirm(
      `Delete ${selectedFiles.size} file${selectedFiles.size > 1 ? "s" : ""}? This cannot be undone.`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const response = await authFetchJson(apiUrl("/api/c3/files/delete-bulk"), {
        method: "POST",
        body: JSON.stringify({ paths: Array.from(selectedFiles) }),
      });

      if (response.success) {
        clearSelection();
        fetchFiles();
      }
    } catch (err) {
      console.error("[FileBrowser] Bulk delete error:", err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Build tree structure from flat file list
  const buildTree = (items) => {
    const tree = { children: {}, files: [] };

    if (!Array.isArray(items)) {
      console.warn("[FileBrowser] buildTree received non-array:", items);
      return tree;
    }

    items.forEach((item) => {
      if (!item || !item.path || typeof item.path !== "string") {
        return;
      }

      const parts = item.path.split("/").filter(Boolean);
      if (parts.length === 0) return;

      let current = tree;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current.children[part]) {
          current.children[part] = { children: {}, files: [] };
        }
        current = current.children[part];
      }

      if (item.type === "folder") {
        const folderName = parts[parts.length - 1];
        if (folderName && !current.children[folderName]) {
          current.children[folderName] = { children: {}, files: [] };
        }
      } else {
        current.files.push(item);
      }
    });

    return tree;
  };

  // Filter files by search query
  const filterFiles = (items) => {
    if (!Array.isArray(items)) return [];
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((item) => {
      if (!item) return false;
      const name = item.name || "";
      const path = item.path || "";
      return name.toLowerCase().includes(query) || path.toLowerCase().includes(query);
    });
  };

  // ============ DRAG & DROP MOVE (react-beautiful-dnd) ============
  // Note: MinIO doesn't support ordering - files are sorted alphabetically.
  // This drag/drop only supports MOVING files between folders.

  const handleDragEnd = async (result) => {
    console.log("[FileBrowser] Drag end:", result);

    if (!result.destination) {
      console.log("[FileBrowser] No destination - drag cancelled");
      return;
    }

    const sourceId = result.source.droppableId;
    const destId = result.destination.droppableId;
    const draggedPath = result.draggableId;

    console.log("[FileBrowser] Move:", { from: sourceId, to: destId, path: draggedPath });

    // If dropped in same folder, nothing to do (no reordering in MinIO)
    if (sourceId === destId) {
      console.log("[FileBrowser] Same folder - no action needed");
      return;
    }

    // Calculate new path based on destination folder
    const fileName = draggedPath.split("/").pop();
    const destFolder = destId === "root" ? "" : destId;
    const newPath = destFolder ? `${destFolder}/${fileName}` : fileName;

    console.log("[FileBrowser] New path:", newPath);

    // Don't move if same path (shouldn't happen but safety check)
    if (draggedPath === newPath) {
      console.log("[FileBrowser] Same path - no action needed");
      return;
    }

    // Check if it's a folder being moved
    const draggedItem = files.find((f) => f.path === draggedPath);
    const isFolder = draggedItem?.type === "folder";

    try {
      console.log("[FileBrowser] Calling rename API:", { oldPath: draggedPath, newPath, type: isFolder ? "folder" : "file" });

      const response = await authFetchJson(apiUrl("/api/c3/files/rename"), {
        method: "POST",
        body: JSON.stringify({
          oldPath: draggedPath,
          newPath,
          type: isFolder ? "folder" : "file",
        }),
      });

      console.log("[FileBrowser] Rename response:", response);

      if (response.success) {
        fetchFiles();
      } else {
        console.error("[FileBrowser] Move failed:", response.error);
      }
    } catch (err) {
      console.error("[FileBrowser] Move error:", err);
    }
  };

  // ============ UPLOAD HANDLERS ============

  // Size limits: audio/video can be larger (only transcript stored)
  const MAX_MEDIA_SIZE = 500 * 1024 * 1024; // 500MB for audio/video
  const MAX_FILE_SIZE = 50 * 1024 * 1024;   // 50MB for other files

  const isMediaFile = (file) => {
    const type = file.type || '';
    return type.startsWith('audio/') || type.startsWith('video/');
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;

    // Client-side size validation (fail fast, better UX)
    const oversizedFiles = Array.from(fileList).filter(f => {
      const limit = isMediaFile(f) ? MAX_MEDIA_SIZE : MAX_FILE_SIZE;
      return f.size > limit;
    });
    if (oversizedFiles.length > 0) {
      const names = oversizedFiles.map(f => {
        const limit = isMediaFile(f) ? 500 : 50;
        return `"${f.name}" (${(f.size / 1024 / 1024).toFixed(1)}MB, max ${limit}MB)`;
      }).join(', ');
      setUploadMessage(`File too large: ${names}`);
      setTimeout(() => setUploadMessage(null), 8000);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadMessage(null);

    try {
      const token = await getToken();
      const results = [];
      let rateLimited = false;

      for (let i = 0; i < fileList.length; i++) {
        // Stop if we hit rate limit
        if (rateLimited) break;

        const file = fileList[i];
        const formData = new FormData();
        formData.append("file", file);

        try {
          const result = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (e) => {
              if (e.lengthComputable) {
                const fileProgress = (i / fileList.length) * 100 + (e.loaded / e.total) * (100 / fileList.length);
                setUploadProgress(Math.round(fileProgress));
              }
            });

            xhr.addEventListener("load", () => {
              const response = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(response);
              } else if (xhr.status === 429) {
                // Rate limited - show friendly message
                reject({ isRateLimit: true, message: response.error, retryAfter: response.retryAfter });
              } else if (xhr.status === 409 && response.existingFile) {
                // Duplicate file - ask user to confirm overwrite
                reject({ isDuplicate: true, filename: response.filename, file });
              } else {
                reject(new Error(response.error || `Upload failed: ${xhr.statusText}`));
              }
            });

            xhr.addEventListener("error", () => reject(new Error("Upload failed")));

            xhr.open("POST", apiUrl("/api/c3/files/upload"));
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.send(formData);
          });

          results.push(result);

          // Handle queued transcription jobs (audio/video)
          if (result.queued && result.jobId) {
            console.log(`[FileBrowser] Audio/video queued for transcription: ${result.filename} (job: ${result.jobId})`);
            setPendingJobs(prev => {
              const next = new Map(prev);
              next.set(result.jobId, {
                filename: result.filename,
                originalName: result.originalName,
                status: 'pending',
                progress: 0,
              });
              return next;
            });
            setUploadMessage(`Transcribing "${result.filename}"... This may take a moment.`);
          }

          // Log direct transcription results (legacy path)
          if (result.transcribed) {
            console.log(`[FileBrowser] Audio/video transcribed: ${result.originalName} → ${result.path}`);
          }
        } catch (fileErr) {
          if (fileErr.isRateLimit) {
            // Show rate limit message and stop uploading
            rateLimited = true;
            setUploadMessage(fileErr.message);
            console.log(`[FileBrowser] Rate limited: ${fileErr.message}`);
            // Auto-clear message after retry period
            setTimeout(() => setUploadMessage(null), (fileErr.retryAfter || 30) * 1000);
          } else if (fileErr.isDuplicate) {
            // Ask user to confirm overwrite
            const confirmOverwrite = window.confirm(
              `"${fileErr.filename}" already exists. Do you want to replace it?`
            );
            if (confirmOverwrite) {
              // Re-upload with overwrite flag
              try {
                const overwriteResult = await uploadWithOverwrite(fileErr.file, token);
                results.push(overwriteResult);
              } catch (overwriteErr) {
                console.warn(`[FileBrowser] Overwrite failed:`, overwriteErr.message);
              }
            }
          } else {
            // Show error to user (file too large, server error, etc.)
            const errorMsg = fileErr.message || "Upload failed";
            console.warn(`[FileBrowser] Failed to upload ${file.name}:`, errorMsg);
            setUploadMessage(`Failed to upload "${file.name}": ${errorMsg}`);
            setTimeout(() => setUploadMessage(null), 6000);
          }
        }
      }

      fetchFiles();
    } catch (err) {
      console.error("[FileBrowser] Upload error:", err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (e) => {
    console.log("[FileBrowser] File input changed:", e.target.files?.length, "files");
    if (e.target.files && e.target.files.length > 0) {
      // Copy files BEFORE clearing input (FileList is live and gets emptied)
      const files = Array.from(e.target.files);
      e.target.value = ""; // Clear first to allow re-uploading same file
      handleFiles(files);
    }
  };

  // Click handler for upload button - programmatically triggers file input
  const handleUploadClick = () => {
    console.log("[FileBrowser] Upload button clicked");
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  // Helper to upload with overwrite flag (for duplicate confirmations)
  const uploadWithOverwrite = async (file, token) => {
    const formData = new FormData();
    formData.append("file", file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", () => {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Overwrite failed"));
        }
      });
      xhr.addEventListener("error", () => reject(new Error("Overwrite failed")));
      xhr.open("POST", apiUrl("/api/c3/files/upload?overwrite=true"));
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);
    });
  };

  // ============ FILE DRAG & DROP (upload) ============

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag active for file drops, not internal drags
    if (e.dataTransfer?.types?.includes("Files")) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Check for folder drops using webkitGetAsEntry
    const items = e.dataTransfer?.items;
    if (items?.length) {
      const entries = [];
      for (const item of items) {
        const entry = item.webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }

      // If any entry is a directory, extract files recursively
      const hasFolder = entries.some(e => e.isDirectory);
      if (hasFolder) {
        const filesWithPaths = await extractFilesFromEntries(entries);
        if (filesWithPaths.length > 0) {
          await handleFilesWithPaths(filesWithPaths);
        }
        return;
      }
    }

    // Regular file drop
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Extract files recursively from folder entries
  const extractFilesFromEntries = async (entries) => {
    const files = [];

    const traverseEntry = async (entry, path = '') => {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) => {
          entry.file(resolve, reject);
        });
        files.push({ file, path: path + file.name });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readEntries = () => new Promise((resolve, reject) => {
          reader.readEntries(resolve, reject);
        });

        // readEntries may return partial results, so read until empty
        let allEntries = [];
        let batch;
        do {
          batch = await readEntries();
          allEntries = allEntries.concat(batch);
        } while (batch.length > 0);

        for (const childEntry of allEntries) {
          await traverseEntry(childEntry, path + entry.name + '/');
        }
      }
    };

    for (const entry of entries) {
      await traverseEntry(entry);
    }

    return files;
  };

  // Handle files that have path info (from folder upload)
  const handleFilesWithPaths = async (filesWithPaths) => {
    // Filter out hidden files
    const visible = filesWithPaths.filter(f => !f.file.name.startsWith('.') && !f.path.includes('/.'));

    // Validation
    const MAX_FOLDER_FILES = 100;
    const MAX_FOLDER_SIZE = 200 * 1024 * 1024; // 200MB total for folders

    const totalSize = visible.reduce((sum, f) => sum + f.file.size, 0);
    const skippedHidden = filesWithPaths.length - visible.length;

    if (visible.length === 0) {
      setUploadMessage('No uploadable files found in folder (only hidden files?)');
      setTimeout(() => setUploadMessage(null), 5000);
      return;
    }

    if (visible.length > MAX_FOLDER_FILES) {
      setUploadMessage(`Too many files: ${visible.length} (max ${MAX_FOLDER_FILES}). Try uploading in smaller batches.`);
      setTimeout(() => setUploadMessage(null), 8000);
      return;
    }

    if (totalSize > MAX_FOLDER_SIZE) {
      const sizeMB = (totalSize / 1024 / 1024).toFixed(1);
      setUploadMessage(`Folder too large: ${sizeMB}MB (max 200MB). Try uploading large files separately.`);
      setTimeout(() => setUploadMessage(null), 8000);
      return;
    }

    // Show progress message
    if (skippedHidden > 0) {
      setUploadMessage(`Uploading ${visible.length} files (skipped ${skippedHidden} hidden files)...`);
    } else {
      setUploadMessage(`Uploading ${visible.length} files from folder...`);
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = await getToken();
      let completed = 0;
      let failed = [];

      for (const { file, path } of visible) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("path", path); // Include folder structure

          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (e) => {
              if (e.lengthComputable) {
                const fileProgress = (completed / visible.length) * 100 +
                  (e.loaded / e.total) * (100 / visible.length);
                setUploadProgress(Math.round(fileProgress));
              }
            });

            xhr.addEventListener("load", () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
              } else if (xhr.status === 429) {
                reject(new Error('Rate limited - please wait and try again'));
              } else {
                const resp = JSON.parse(xhr.responseText);
                reject(new Error(resp.error || xhr.statusText));
              }
            });

            xhr.addEventListener("error", () => reject(new Error("Network error")));

            xhr.open("POST", apiUrl("/api/c3/files/upload"));
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.send(formData);
          });

          completed++;
          setUploadMessage(`Uploading folder: ${completed}/${visible.length} files...`);
        } catch (err) {
          failed.push({ path, error: err.message });
          console.warn(`[FileBrowser] Failed to upload ${path}:`, err.message);
        }
      }

      // Summary message
      if (failed.length === 0) {
        setUploadMessage(`Uploaded ${completed} files successfully`);
      } else {
        setUploadMessage(`Uploaded ${completed} files, ${failed.length} failed`);
        console.warn('[FileBrowser] Failed uploads:', failed);
      }

      setTimeout(() => setUploadMessage(null), 5000);
      fetchFiles();
    } catch (err) {
      console.error("[FileBrowser] Folder upload error:", err);
      setUploadMessage(`Upload failed: ${err.message}`);
      setTimeout(() => setUploadMessage(null), 6000);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ============ NEW FOLDER HANDLER ============

  const handleNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      setShowNewFolderInput(false);
      return;
    }

    setCreatingFolder(true);

    try {
      const response = await authFetchJson(apiUrl("/api/c3/files/mkdir"), {
        method: "POST",
        body: JSON.stringify({ path: name }),
      });

      if (response.success) {
        fetchFiles();
      }
    } catch (err) {
      console.error("[FileBrowser] Create folder error:", err);
    } finally {
      setCreatingFolder(false);
      setShowNewFolderInput(false);
      setNewFolderName("");
    }
  };

  const handleNewFolderKeyDown = (e) => {
    if (e.key === "Enter") {
      handleNewFolder();
    } else if (e.key === "Escape") {
      setShowNewFolderInput(false);
      setNewFolderName("");
    }
  };

  // Render tree recursively with drag/drop
  // Each folder is wrapped in its own Droppable so it can accept drops even when collapsed
  const renderTree = (node, path = "", depth = 0) => {
    const elements = [];
    const folderNames = Object.keys(node.children).sort();
    const sortedFiles = [...node.files].sort((a, b) => a.name.localeCompare(b.name));

    // Folder entries - each folder is a Droppable that can accept items
    folderNames.forEach((name, index) => {
      const folderPath = path ? `${path}/${name}` : name;
      const folder = node.children[name];
      const hasChildren = Object.keys(folder.children).length > 0 || folder.files.length > 0;
      const isExpanded = expandedFolders.has(folderPath);
      const color = getFolderColor(folderPath);

      elements.push(
        <Droppable key={`folder-drop-${folderPath}`} droppableId={folderPath}>
          {(dropProvided, dropSnapshot) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              style={{
                background: dropSnapshot.isDraggingOver ? `${color}20` : "transparent",
                borderRadius: "4px",
                transition: "background 0.15s ease",
              }}
            >
              <Draggable key={`folder-${folderPath}`} draggableId={folderPath} index={index}>
                {(dragProvided, dragSnapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    style={{
                      ...dragProvided.draggableProps.style,
                      opacity: dragSnapshot.isDragging ? 0.8 : 1,
                    }}
                  >
                    <FileItem
                      name={name}
                      path={folderPath}
                      type="folder"
                      depth={depth}
                      isExpanded={isExpanded}
                      hasChildren={hasChildren}
                      onToggle={() => toggleFolder(folderPath)}
                      onRefresh={fetchFiles}
                      folderColor={color}
                      onColorClick={() => openColorModal(folderPath)}
                      onShareClick={() => openShareModal(folderPath)}
                      isDragOver={dropSnapshot.isDraggingOver}
                      onUseInChat={onUseInChat ? () => onUseInChat(folderPath, true) : null}
                    />
                  </div>
                )}
              </Draggable>
              {/* Render folder contents when expanded */}
              {isExpanded && renderTree(folder, folderPath, depth + 1)}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      );
    });

    // File entries
    sortedFiles.forEach((file, index) => {
      const fileIndex = folderNames.length + index;
      elements.push(
        <Draggable key={`file-${file.path}`} draggableId={file.path} index={fileIndex}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={{
                ...provided.draggableProps.style,
                opacity: snapshot.isDragging ? 0.8 : 1,
              }}
            >
              <FileItem
                name={file.name}
                path={file.path}
                type="file"
                size={file.size}
                mimeType={file.mimeType}
                lastModified={file.lastModified}
                depth={depth}
                onRefresh={fetchFiles}
                onUseInChat={onUseInChat ? () => onUseInChat(file.path, false) : null}
                selectionMode={selectionMode}
                isSelected={selectedFiles.has(file.path)}
                onSelect={(e) => handleFileSelect(file.path, e)}
              />
            </div>
          )}
        </Draggable>
      );
    });

    return elements;
  };

  // Don't render if collapsed
  if (isCollapsed) {
    return null;
  }

  // Build tree
  let tree = { children: {}, files: [] };
  try {
    tree = buildTree(filterFiles(files));
  } catch (err) {
    console.error("[FileBrowser] Error building tree:", err);
  }

  return (
    <div
      className={`file-browser ${dragActive ? "drag-active" : ""}`}
      onDrag={handleDrag}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file input - simple off-screen positioning */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
        }}
      />

      {/* File drop overlay */}
      {dragActive && (
        <div className="drag-overlay">
          <Upload size={32} />
          <span>Drop files to upload</span>
        </div>
      )}

      {/* Folder color modal */}
      {showColorModal && (
        <>
          <div className="color-modal-backdrop" onClick={() => setShowColorModal(false)} />
          <div className="color-modal">
            <div className="color-modal-header">
              <h4>Folder Color</h4>
              <button className="color-modal-close" onClick={() => setShowColorModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="color-picker">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`color-btn ${getFolderColor(colorModalFolder) === color ? "selected" : ""}`}
                  style={{ background: color }}
                  onClick={() => setFolderColor(colorModalFolder, color)}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Share-with-team modal */}
      {showShareModal && (
        <>
          <div className="color-modal-backdrop" onClick={() => setShowShareModal(false)} />
          <div className="color-modal" style={{ minWidth: 320 }}>
            <div className="color-modal-header">
              <h4>Share "{shareModalFolder}"</h4>
              <button className="color-modal-close" onClick={() => setShowShareModal(false)}>
                <X size={16} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "4px 0 10px" }}>
              Team members and their agents get <strong>read/write</strong> in this folder (never delete).
            </p>
            {myTeams === null ? (
              <p style={{ fontSize: 13 }}>Loading teams…</p>
            ) : myTeams.length === 0 ? (
              <p style={{ fontSize: 13 }}>No teams yet — create one in Settings → 👥 Team.</p>
            ) : (
              myTeams.map((t) => {
                const isShared = (folderShares[t.id] || new Set()).has(shareModalFolder);
                return (
                  <button
                    key={t.id}
                    disabled={shareBusy}
                    onClick={() => toggleFolderShare(t.id, shareModalFolder, isShared)}
                    style={{
                      display: "flex", justifyContent: "space-between", width: "100%",
                      padding: "8px 12px", marginBottom: 6, borderRadius: 8, cursor: "pointer",
                      border: "1px solid var(--border-primary)",
                      background: isShared ? "rgba(34,211,238,0.12)" : "transparent",
                      color: "var(--text-primary)",
                    }}
                  >
                    <span>👥 {t.name}</span>
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                      {isShared ? "Shared ✓ (tap to unshare)" : "Share"}
                    </span>
                  </button>
                );
              })
            )}
            {shareMsg && <p style={{ fontSize: 12, marginTop: 6, color: "var(--text-secondary)" }}>{shareMsg}</p>}
          </div>
        </>
      )}

      {/* Header */}
      <div className="file-browser-header">
        {selectionMode ? (
          <>
            {/* Selection mode controls */}
            <button
              type="button"
              className="select-action-btn"
              onClick={selectAll}
              title="Select all files"
            >
              <CheckSquare size={14} />
              <span>All</span>
            </button>
            <button
              type="button"
              className="select-action-btn"
              onClick={clearSelection}
              title="Clear selection"
            >
              <Square size={14} />
              <span>None</span>
            </button>
            {selectedFiles.size > 0 && (
              <button
                type="button"
                className="select-action-btn danger"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                title={`Delete ${selectedFiles.size} file${selectedFiles.size > 1 ? "s" : ""}`}
              >
                {bulkDeleting ? <Loader size={14} className="spinning" /> : <Trash2 size={14} />}
                <span>{selectedFiles.size}</span>
              </button>
            )}
            <button
              type="button"
              className="select-mode-btn active"
              onClick={toggleSelectionMode}
              title="Exit selection mode"
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <>
            {/* Normal mode controls */}
            <button
              type="button"
              className={`file-upload-btn ${uploading ? "uploading" : ""}`}
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader size={16} className="spinning" />
                  <span>{uploadProgress}%</span>
                </>
              ) : (
                <>
                  <Upload size={16} />
                  <span>Upload</span>
                </>
              )}
            </button>
            <button
              type="button"
              className="new-folder-btn"
              onClick={() => setShowNewFolderInput(true)}
              disabled={creatingFolder}
              title="New folder"
            >
              <FolderPlus size={16} />
            </button>
            <button
              type="button"
              className="select-mode-btn"
              onClick={toggleSelectionMode}
              title="Select multiple files"
            >
              <CheckSquare size={14} />
            </button>
          </>
        )}
      </div>

      {/* Upload rate limit message */}
      {uploadMessage && (
        <div className="upload-message">
          {uploadMessage}
        </div>
      )}

      {/* Pending transcription jobs */}
      {pendingJobs.size > 0 && (
        <div className="transcription-jobs">
          {Array.from(pendingJobs.entries()).map(([jobId, job]) => (
            <div key={jobId} className="transcription-job">
              <Loader size={12} className="spinning" />
              <span className="job-filename" title={job.originalName || job.filename}>
                {job.filename}
              </span>
              <span className="job-progress">{job.progress || 0}%</span>
            </div>
          ))}
        </div>
      )}

      {/* New folder input */}
      {showNewFolderInput && (
        <div className="new-folder-input-row">
          <input
            ref={newFolderInputRef}
            type="text"
            placeholder="Folder name..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={handleNewFolderKeyDown}
            onBlur={handleNewFolder}
            disabled={creatingFolder}
            className="new-folder-input"
          />
        </div>
      )}

      {/* Search */}
      <div className="file-browser-search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="file-search-input"
        />
      </div>

      {/* File tree */}
      <div className="file-browser-content">
        {loading ? (
          <div className="file-browser-loading">Loading files...</div>
        ) : error ? (
          <div className="file-browser-error">
            <span>{error}</span>
            <button onClick={handleRefresh}>Retry</button>
          </div>
        ) : files.length === 0 ? (
          <div className="file-browser-empty">
            <Upload size={32} className="empty-icon" />
            <p>No files yet</p>
            <p className="hint">Drag files here or click Upload</p>
            <p className="hint">Audio/video auto-transcribed to text</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="root">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="file-tree"
                  style={{
                    background: snapshot.isDraggingOver ? "rgba(0, 122, 255, 0.05)" : "transparent",
                    borderRadius: "4px",
                    transition: "background 0.2s",
                  }}
                >
                  {renderTree(tree)}

                  {/* Shared with me — team folders (r/w via team/ paths) */}
                  {sharedWithMe.length > 0 && (
                    <div style={{ marginTop: 14, borderTop: "1px solid var(--border-primary)", paddingTop: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-secondary)", padding: "2px 8px 6px" }}>
                        👥 SHARED WITH ME
                      </div>
                      {sharedWithMe.map((f) => {
                        const key = `${f.team_id}/${f.folder_path}`;
                        const open = sharedOpen[key];
                        return (
                          <div key={key}>
                            <div
                              onClick={() => toggleSharedFolder(f)}
                              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 10px", cursor: "pointer", fontSize: 13, borderRadius: 6 }}
                              title={`Shared by your team "${f.team_name}" — read/write for you and your agents`}
                            >
                              <span>📁 {f.folder_path}</span>
                              <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{f.team_name} · r/w</span>
                            </div>
                            {open && (
                              <div style={{ paddingLeft: 22 }}>
                                {open.loading ? (
                                  <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: "3px 0" }}>Loading…</div>
                                ) : open.files.length === 0 ? (
                                  <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: "3px 0" }}>Empty so far — you and your agents can write here.</div>
                                ) : (
                                  open.files.map((file) => (
                                    <div
                                      key={file.path}
                                      onClick={() => downloadSharedFile(file.path)}
                                      style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "3px 8px", cursor: "pointer", borderRadius: 5 }}
                                      title="Click to download"
                                    >
                                      <span>{(file.path || "").split("/").slice(3).join("/") || file.name}</span>
                                      <span style={{ color: "var(--text-secondary)" }}>{file.size ? `${Math.max(1, Math.round(file.size / 1024))} KB` : ""}</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default FileBrowser;
